// Licensing presentation adapter (Cloud side of the shared Partner + Cloud model).
//
// Pure: it turns the reads Cloud actually has (GET /billing, the v1.15 GET /billing/licensing, org module flags, site
// names) into the shared vocabulary.
// It never invents a fact. Anything the reads do not supply comes out as UNKNOWN / null and renders as "Not
// confirmed", never as No licence, Off, Paid or Nothing to do. See docs/licensing-ux-data-map.md for the
// fact → read mapping and the MISSING list.
//
// Configuration (the module flag: On/Off) is a separate fact from licence. On ≠ licensed; licensed ≠ On.

export const LICENCE_STATE = Object.freeze({
	ONGOING: 'ONGOING',
	AGREED: 'AGREED',
	TRIAL: 'TRIAL',
	GRACE: 'GRACE',
	NONE: 'NONE',
	UNKNOWN: 'UNKNOWN'
});

export const PHASE = Object.freeze({ SETUP: 'SETUP', ONGOING: 'ONGOING', UNKNOWN: 'UNKNOWN' });

export const OWNER = Object.freeze({
	PARTNER: 'PARTNER',
	INSTALLER: 'INSTALLER',
	CUSTOMER: 'CUSTOMER',
	ZEZAMII: 'ZEZAMII',
	UNKNOWN: 'UNKNOWN'
});

export const OWNER_LABEL = Object.freeze({
	PARTNER: 'Your partner',
	INSTALLER: 'Your installer',
	CUSTOMER: 'You',
	ZEZAMII: 'Zezamii',
	UNKNOWN: 'Not confirmed'
});

export const NOT_CONFIRMED = 'Not confirmed';
// Customer module trials (site-trial-billing v1.16) are not deployed: Cloud has no trial-start capability, so it
// never starts or fakes one. The item is available to try "coming soon", through the partner.
export const TRIALS_UNAVAILABLE = 'Trying this from Cloud is coming soon. Ask your partner.';
export const SITE_TRIAL_MODEL = 'ACCESS_PER_LOCK_V2';

// Absent-by-default capability on the billing read. No backend sends it today; when one does, the trial notice can
// show the offer. There is no trial-start mutation wired in Cloud (see the data map).
export const TRIAL_OFFERS_CAPABILITY = 'customerTrialOffers';

const LICENCE_LABEL = {
	ONGOING: 'Licensed',
	AGREED: 'Licence agreed',
	TRIAL: 'Trial',
	GRACE: 'Grace period',
	NONE: 'Not licensed',
	UNKNOWN: NOT_CONFIRMED
};

// StatusDot / StatusPill tone per state. UNKNOWN is a hollow ring (indeterminate), NONE the only muted one.
export const LICENCE_TONE = {
	ONGOING: 'success',
	AGREED: 'info',
	TRIAL: 'info',
	GRACE: 'warning',
	NONE: 'muted',
	UNKNOWN: 'neutral'
};

const UNIT_LABEL = {
	LOCK_TENTHS: ['lock', 'locks'],
	SITE: ['site', 'sites'],
	ACCESS_PANEL: ['panel', 'panels'],
	ROOM: ['room', 'rooms'],
	BOOKABLE_UNIT: ['bookable unit', 'bookable units']
};

export const unitLabel = (unit, n) => {
	const [one, many] = UNIT_LABEL[unit] || ['unit', 'units'];
	return n === 1 ? one : many;
};

const EDITION_LABEL = { CORE: 'Core', PRO: 'Pro', ENTERPRISE: 'Enterprise' };

/**
 * Catalogue and parentage (verified mapping from SHARED-MODEL.md + productsConfig + App Server moduleCatalog
 * SLICE2_MODULES). `productKey` joins to config/productsConfig PRODUCTS (name, logo, flag). Lockers and Vision are not
 * in the v1.14 licensing catalogue. Never sum unlike units; never promote a module to a product.
 * Product-licensing plan v0.21 D44 (GB 2026-11-03): Bookings is a MODULE of Access (it does not work without Access);
 * Rooms stays its own product. Top-level products: Access, Rooms, Lockers, Vision. Bookings keeps `productKey:
 * 'bookings'` so its productsConfig logo, flag and On/Off switch still join.
 */
export const LICENSING_CATALOGUE = Object.freeze([
	{
		key: 'access',
		productKey: 'access',
		codes: ['ACCESS'],
		unit: 'LOCK_TENTHS',
		inLicensingCatalogue: true,
		modules: [
			{
				key: 'visitorManagement',
				productKey: 'visitorManagement',
				label: 'Visitor Management',
				codes: ['VISITOR_MANAGEMENT'],
				unit: 'SITE'
			},
			{
				key: 'bookings',
				productKey: 'bookings',
				label: 'Bookings',
				codes: ['BOOKINGS'],
				unit: 'BOOKABLE_UNIT'
			},
			{
				key: 'integrations',
				productKey: null,
				label: 'Access control integrations',
				codes: ['INTEGRATIONS'],
				// Plan D48 (GB 2026-11-03): Integrations is licensed per SITE. A line or catalogue entry from the server
				// carries its own unit (older lines may still say ACCESS_PANEL), and that is what is shown.
				unit: 'SITE',
				inLegacyCatalogue: false
			}
		]
	},
	{ key: 'rooms', productKey: 'rooms', codes: ['ROOMS'], unit: 'ROOM', inLicensingCatalogue: true, modules: [] },
	{ key: 'lockers', productKey: 'lockers', codes: ['LOCKERS'], unit: null, inLicensingCatalogue: false, modules: [] },
	{
		key: 'vision',
		productKey: 'vision',
		codes: ['CAMERAS', 'VISION', 'VISION_AI'],
		unit: null,
		inLicensingCatalogue: false,
		modules: []
	}
]);

// ── small helpers ────────────────────────────────────────────────────────────────────────────────

const licence = (state, extra = {}) => ({ state, label: LICENCE_LABEL[state], tone: LICENCE_TONE[state], ...extra });

const unknownLicence = (reason) => licence(LICENCE_STATE.UNKNOWN, { reason });

/**
 * GB 2026-11-03 (v1.15.2 C-D5): an org with no v1.14 agreement (no billing record, or a legacy Cloud plan) holds Access
 * under its legacy arrangement, so Access reads Licensed, marked Legacy. Only after a SUCCESSFUL billing read: a failed
 * or refused read stays Not confirmed (INV-C7).
 */
export const LEGACY_ACCESS_LABEL = 'Licensed · Legacy';
const legacyAccessLicence = () =>
	licence(LICENCE_STATE.ONGOING, {
		label: LEGACY_ACCESS_LABEL,
		legacy: true,
		detail: 'Licensed under your legacy arrangement, from before per-lock licensing.'
	});

const earliest = (dates) =>
	dates
		.filter(Boolean)
		.slice()
		.sort((a, b) => new Date(a) - new Date(b))[0] || null;

// A labelled date, by authoritative event type only. value null → "Not confirmed" / "not set" in the UI.
const date = (kind, label, value) => ({ kind, label, value: value ?? null });

const unknownCapacity = (unit) => ({
	unit,
	licensed: null,
	licensedIsEstimate: null,
	inUse: null,
	balance: null,
	// Access only (refined UX 2026-11-03): installed/placed billable locks, and those not yet counted for licensing.
	placed: null,
	notCounted: null,
	exceptions: []
});

const configurationOf = (productKey, moduleEnabled) => {
	if (!productKey || !moduleEnabled) return { on: null };
	const v = moduleEnabled[productKey];
	return { on: typeof v === 'boolean' ? v : null };
};

const trialOf = (codes, licenceState, billing) => {
	const offers = Array.isArray(billing?.[TRIAL_OFFERS_CAPABILITY]) ? billing[TRIAL_OFFERS_CAPABILITY] : null;
	const offer = offers ? offers.find((o) => o && codes.includes(o.code) && o.eligible === true) || null : null;
	const held = [LICENCE_STATE.ONGOING, LICENCE_STATE.TRIAL, LICENCE_STATE.GRACE, LICENCE_STATE.AGREED].includes(
		licenceState
	);
	if (held) return null;
	// No trial mutation exists in Cloud, so even an offer cannot be started here (canStart stays false).
	return { offer, canStart: false, message: TRIALS_UNAVAILABLE };
};

// ── v1.15 customer licensing read (GET /billing/licensing) ─────────────────────────────────────────

/** v1.15 commitment states that mean set-up (before attach) vs ongoing (attached or licensed through processing). */
const SETUP_STATES = ['QUOTED', 'ACCEPTED'];
const ONGOING_STATES = ['ACTIVE', 'LICENSED_PROCESSING'];
const LOCK_TENTHS_PER_LOCK = 10;

/**
 * The customer licensing record when (and only when) Cloud may rely on it: a 200 (`status: 'loaded'`) that is not
 * redacted and carries a state and a catalogue. A 404 ('hidden'), a failure ('unavailable'), loading or a redacted
 * read gives null, and every caller then keeps today's unknown / legacy path (INV-C7).
 */
export const licensingRecordOf = (licensing) => {
	if (licensing?.status !== 'loaded') return null;
	const d = licensing.data;
	if (!d || d.redacted === true || typeof d.state !== 'string' || !Array.isArray(d.catalog)) return null;
	return d;
};

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
const commitmentsOf = (record) => (record ? [record.initial, ...(record.additions || [])].filter(Boolean) : []);
const commitmentLinesFor = (record, codes) =>
	commitmentsOf(record).flatMap((c) => (c.lines || []).filter((l) => codes.includes(l.catalogItemCode)));
const sumOf = (xs, f) => xs.reduce((n, x) => n + (Number(f(x)) || 0), 0);
/** Tenths of a lock (the wire unit) → locks (the display unit `unitLabel('LOCK_TENTHS')` names); null stays null. */
const locksOf = (tenths) => (typeof tenths === 'number' ? tenths / LOCK_TENTHS_PER_LOCK : null);
const locksText = (locks) => `${Number.isInteger(locks) ? locks : locks.toFixed(1)} ${locks === 1 ? 'lock' : 'locks'}`;

/** Phase from the commitment state: SETUP before attach, ONGOING once attached / processed; otherwise UNKNOWN. */
const phaseOf = (record) => {
	if (!record) return PHASE.UNKNOWN;
	if (ONGOING_STATES.includes(record.state)) return PHASE.ONGOING;
	if (SETUP_STATES.includes(record.state)) return PHASE.SETUP;
	return PHASE.UNKNOWN;
};

// ── source classification ────────────────────────────────────────────────────────────────────────

/**
 * Which authoritative record (if any) this org's billing read gives us.
 *   UNAVAILABLE — read failed / refused / not loaded
 *   REDACTED    — v1.14 org, viewer not shown detail
 *   V114        — v1.14 per-lock agreement with detail
 *   LEGACY_PLAN — legacy Cloud plan with entitlements
 *   NONE        — read succeeded, no plan and no agreement (unprovisioned / grandfathered): nothing confirmed
 */
export const classifySource = ({ billing, status }) => {
	if (status !== 'loaded' || !billing) return 'UNAVAILABLE';
	if (billing.commercialModel === SITE_TRIAL_MODEL && billing.agreement && typeof billing.agreement === 'object') {
		return billing.agreement.state == null ? 'REDACTED' : 'V114';
	}
	if (billing.plan) return 'LEGACY_PLAN';
	return 'NONE';
};

// ── v1.14 ────────────────────────────────────────────────────────────────────────────────────────

const accessFromAgreement = (a, siteNames, record = null) => {
	const coverage = new Map((record?.sites || []).map((s) => [String(s.siteID), s]));
	const sites = (a.sites || []).map((s) => ({
		siteID: s.siteID,
		name: siteNames?.[String(s.siteID)] || `Site ${s.siteID}`,
		graceStartsAt: s.trial?.startsAt ?? null,
		graceEndsAt: s.trial?.endsAt ?? null,
		gate: s.gate || null,
		// Placed vs counted billable locks per site (in locks), from the v1.15 licensing read; null without it.
		placed: locksOf(coverage.get(String(s.siteID))?.placedBillable),
		counted: locksOf(coverage.get(String(s.siteID))?.counted)
	}));
	// a site the licensing read covers that the agreement's site list does not (yet) carry
	for (const c of coverage.values()) {
		if (sites.some((s) => String(s.siteID) === String(c.siteID))) continue;
		sites.push({
			siteID: c.siteID,
			name: siteNames?.[String(c.siteID)] || c.siteName || `Site ${c.siteID}`,
			graceStartsAt: null,
			graceEndsAt: c.graceEndsAt ?? null,
			gate: null,
			placed: locksOf(c.placedBillable),
			counted: locksOf(c.counted)
		});
	}
	const started = sites.filter((s) => s.graceStartsAt);

	let lic;
	let when;
	switch (a.state) {
		case 'TRIAL':
			if (started.length > 0) {
				lic = licence(LICENCE_STATE.GRACE);
				when = date('GRACE_ENDS', 'Grace ends', earliest(started.map((s) => s.graceEndsAt)));
			} else {
				lic = licence(LICENCE_STATE.AGREED, { detail: 'Waiting for installation' });
				when = date('RENEWAL', 'Renewal date', null);
				when.emptyLabel = 'Renewal date not set';
			}
			break;
		case 'ACTIVE':
			lic = licence(LICENCE_STATE.ONGOING);
			// v1.15: the licensing read states the renewal date; without it the billing read has only the current period
			// end, labelled as what it is.
			when = record?.dates?.renewalAt
				? date('RENEWAL', 'Renewal date', record.dates.renewalAt)
				: date('PERIOD_ENDS', 'Current period ends', a.periodEndsAt);
			break;
		case 'CANCEL_SCHEDULED':
			lic = licence(LICENCE_STATE.ONGOING);
			when = date('ENDS', 'Ends', a.pendingChange?.effectiveAt ?? a.periodEndsAt);
			break;
		case 'ENDED':
			lic = licence(LICENCE_STATE.NONE, { label: 'Ended' });
			when = record?.dates?.endedAt ? date('ENDED', 'Ended', record.dates.endedAt) : null;
			break;
		default:
			lic = unknownLicence('Agreement status not recognised.');
			when = null;
	}

	return {
		licence: lic,
		edition: EDITION_LABEL[a.edition] || null,
		date: when,
		capacity: record ? accessCapacity(record, sites) : unknownCapacity('LOCK_TENTHS'),
		sites
	};
};

/**
 * v1.15 Access capacity: licensed = the live initial's Access line (an estimate), in use = the counted locks
 * (`status.locksInstalledTenths`, absent = not confirmed), and per-site placed-but-not-counted locks as an exception
 * the partner reviews. The wire is in tenths of a lock; everything here is in LOCKS (the display unit).
 */
const accessCapacity = (record, sites) => {
	const line = commitmentLinesFor(record, ['ACCESS'])[0] || null;
	const licensed = line ? locksOf(line.quantity) : null;
	const st = record.status;
	const inUse = has(st, 'locksInstalledTenths') ? locksOf(st.locksInstalledTenths) : null;
	const exceptions = sites
		.filter((s) => typeof s.placed === 'number' && typeof s.counted === 'number' && s.placed > s.counted)
		.map((s) => ({
			key: 'awaitingReview',
			siteID: s.siteID,
			count: s.placed - s.counted,
			text: `${locksText(s.placed - s.counted)} awaiting review at ${s.name}`,
			owner: OWNER.PARTNER
		}));
	const estimate = line ? line.quantityIsEstimate === true : null;
	const allKnown =
		sites.length > 0 && sites.every((s) => typeof s.placed === 'number' && typeof s.counted === 'number');
	return {
		unit: 'LOCK_TENTHS',
		licensed,
		licensedIsEstimate: estimate,
		inUse,
		// Refined UX (GB 2026-11-03): a quote ESTIMATE is not a licensed quantity, so it never yields a spare or short
		// balance. Only a confirmed (non-estimate) quantity is compared with use.
		balance: licensed != null && inUse != null && estimate === false ? licensed - inUse : null,
		placed: allKnown ? sites.reduce((n, s) => n + s.placed, 0) : null,
		notCounted: allKnown ? sites.reduce((n, s) => n + Math.max(0, s.placed - s.counted), 0) : null,
		exceptions
	};
};

const linesFor = (moduleLines, codes) =>
	Array.isArray(moduleLines) ? moduleLines.filter((l) => l && codes.includes(l.code)) : null;

// A per-unit item (module, Rooms, Bookings) from its v1.14 assignment lines. No lines → UNKNOWN: accepted but
// unassigned capacity is not in this read, so absence is not "not licensed".
const perUnitFromLines = (lines, unit, accessDate, siteNames, record = null, codes = []) => {
	if (record) {
		const committed = commitmentLinesFor(record, codes);
		const assigned = lines || [];
		if (assigned.length === 0) return perUnitFromRecord(record, committed, codes, unit, accessDate);
		const out = perUnitFromLines(assigned, unit, accessDate, siteNames);
		if (committed.length) {
			const licensed = sumOf(committed, (l) => l.quantity);
			const used = sumOf(committed, (l) => l.assignment?.assigned);
			out.capacity = { ...out.capacity, licensed, licensedIsEstimate: false, balance: licensed - used };
		}
		return out;
	}
	if (lines == null)
		return {
			licence: unknownLicence('Not in the licensing record Cloud can read.'),
			date: null,
			capacity: unknownCapacity(unit),
			assignments: []
		};
	if (lines.length === 0) {
		return {
			licence: unknownLicence('No assignment recorded; licensed capacity is not visible here.'),
			date: null,
			capacity: unknownCapacity(unit),
			assignments: []
		};
	}
	const live = lines.filter((l) => l.status !== 'ENDED');
	const inService = live.some((l) => l.status === 'IN_SERVICE');
	const inGrace = live.filter((l) => l.status === 'TRIAL');
	let lic;
	let when = accessDate; // v1.14: paid modules co-term with Access
	if (inService) lic = licence(LICENCE_STATE.ONGOING);
	else if (inGrace.length) {
		lic = licence(LICENCE_STATE.GRACE);
		when = date('GRACE_ENDS', 'Grace ends', earliest(inGrace.map((l) => l.trialEndsAt)));
	} else if (live.length === 0) {
		lic = licence(LICENCE_STATE.NONE, { label: 'Ended' });
		when = null;
	} else lic = unknownLicence('Assignment status not recognised.');

	const review = live.filter((l) => l.needsReview);
	const lineUnit = live[0]?.unitType || unit;
	return {
		licence: lic,
		date: when,
		capacity: {
			unit: lineUnit,
			licensed: null, // purchased quantity is MISSING from customer reads
			licensedIsEstimate: null,
			inUse: live.length,
			balance: null,
			exceptions: review.length
				? [
						{
							key: 'needsReview',
							count: review.length,
							text: `${review.length} ${unitLabel(lineUnit, review.length)} need${review.length === 1 ? 's' : ''} review`,
							owner: OWNER.PARTNER
						}
					]
				: []
		},
		assignments: live.map((l) => ({
			id: l.assignmentID ?? `${l.siteID}-${l.resourceID}`,
			siteID: l.siteID ?? null,
			resourceID: l.resourceID ?? null,
			name:
				l.siteID != null
					? siteNames?.[String(l.siteID)] || `Site ${l.siteID}`
					: l.resourceID != null
						? `Resource ${l.resourceID}`
						: NOT_CONFIRMED,
			status: l.status,
			needsReview: !!l.needsReview
		}))
	};
};

/**
 * v1.15: a per-unit item with no assigned (moduleLines) row, from the licensing record. A commitment line → licence
 * agreed with its quantity and spare; no line and the code LISTED in the record's catalogue → not licensed (the only
 * place Cloud says so, INV-C7); a code the record does not list → still not confirmed.
 */
const perUnitFromRecord = (record, committed, codes, unit, accessDate) => {
	if (committed.length) {
		const licensed = sumOf(committed, (l) => l.quantity);
		const used = sumOf(committed, (l) => l.assignment?.assigned);
		return {
			licence: licence(LICENCE_STATE.AGREED, {
				detail: used > 0 ? `${used} of ${licensed} assigned` : 'Not assigned yet'
			}),
			date: accessDate,
			capacity: {
				unit: committed[0].unitType || unit,
				licensed,
				licensedIsEstimate: false,
				inUse: used,
				balance: licensed - used,
				exceptions: []
			},
			assignments: committed.flatMap((l) =>
				(l.assignment?.assignedTo || []).map((u) => ({
					id: `${u.type}-${u.entityID}`,
					siteID: u.type === 'SITE' ? u.entityID : null,
					resourceID: u.type === 'SITE' ? null : u.entityID,
					name: u.name || NOT_CONFIRMED,
					status: null,
					needsReview: false
				}))
			)
		};
	}
	const listedEntry = (record.catalog || []).find((c) => codes.includes(c.code));
	if (listedEntry)
		return {
			licence: licence(LICENCE_STATE.NONE),
			date: null,
			capacity: { ...unknownCapacity(listedEntry.unit || unit), licensed: 0, inUse: 0, balance: 0 },
			assignments: []
		};
	return {
		licence: unknownLicence('Not in the licensing record Cloud can read.'),
		date: null,
		capacity: unknownCapacity(unit),
		assignments: []
	};
};

// ── legacy plan ──────────────────────────────────────────────────────────────────────────────────

const legacyLicence = (billing, codes) => {
	const ents = billing.entitlements || [];
	const held = ents.filter(
		(e) => codes.includes(e.catalogItemCode) && (e.status === 'ACTIVE' || e.status === 'GRACE')
	);
	if (held.length === 0) return licence(LICENCE_STATE.NONE, { label: 'Not in plan' });
	if (billing.subscription?.status === 'TRIALING') return licence(LICENCE_STATE.TRIAL, { detail: 'Plan trial' });
	const inGrace = held.every((e) => e.status === 'GRACE');
	return licence(LICENCE_STATE.ONGOING, inGrace ? { detail: 'Grace period (as recorded)' } : {});
};

const legacyDate = (billing) => {
	if (billing.subscription?.status === 'TRIALING') return date('TRIAL_ENDS', 'Trial ends', billing.trialEndsAt);
	return date('RENEWAL', 'Renewal date', billing.renewalDate);
};

// ── payer / arrangement ──────────────────────────────────────────────────────────────────────────

export const PAYER = Object.freeze({
	PARTNER: 'PARTNER',
	ZEZAMII: 'ZEZAMII',
	PENDING: 'PENDING',
	LEGACY_CARD: 'LEGACY_CARD',
	LEGACY_INVOICE: 'LEGACY_INVOICE',
	UNKNOWN: 'UNKNOWN'
});

const PAYER_COPY = {
	PARTNER: {
		label: 'Your partner invoices you',
		detail: 'Zezamii invoices your partner. Your partner’s invoice to you is separate, and its status isn’t shown in Zezamii.'
	},
	ZEZAMII: {
		label: 'You pay Zezamii by card',
		detail: 'Collected by Zezamii through Stripe. A saved card is not a payment.'
	},
	PENDING: { label: 'Not chosen yet', detail: 'Who pays hasn’t been chosen yet. Your partner sets this up.' },
	LEGACY_CARD: { label: 'Card subscription', detail: 'Your Cloud plan is paid by card through Stripe.' },
	LEGACY_INVOICE: { label: 'Invoiced by Zezamii', detail: 'Your Cloud plan is invoiced through Xero.' },
	UNKNOWN: { label: NOT_CONFIRMED, detail: 'Who invoices this organisation isn’t available here.' }
};

const payerOf = (source, billing) => {
	let kind = PAYER.UNKNOWN;
	if (source === 'V114') {
		const p = billing.agreement.payer;
		kind = ['PARTNER', 'ZEZAMII', 'PENDING'].includes(p) ? p : PAYER.UNKNOWN;
	} else if (source === 'LEGACY_PLAN')
		kind = billing.subscription?.provider === 'STRIPE' ? PAYER.LEGACY_CARD : PAYER.LEGACY_INVOICE;
	return { kind, ...PAYER_COPY[kind] };
};

// ── actions ──────────────────────────────────────────────────────────────────────────────────────

const actionsOf = (source, billing, access, items) => {
	const actions = [];
	if (source === 'V114') {
		const a = billing.agreement;
		if (a.cardState === 'MISSING' && a.payer === 'ZEZAMII') {
			actions.push({
				key: 'saveCard',
				label: 'Save a payment card',
				owner: OWNER.CUSTOMER,
				deadline: a.cardWindowEndsAt ?? null
			});
		}
		if ((a.sites || []).some((s) => s.gate === 'BILLING_PAYMENT_FAILED')) {
			actions.push({
				key: 'updateCard',
				label: 'Update the payment card',
				owner: OWNER.CUSTOMER,
				deadline: null
			});
		}
		if (access?.licence.state === LICENCE_STATE.AGREED) {
			actions.push({ key: 'install', label: 'Waiting for installation', owner: OWNER.INSTALLER, deadline: null });
		}
		const review = items.reduce(
			(n, it) =>
				n +
				(it.capacity?.exceptions || []).filter((e) => e.key === 'needsReview').reduce((m, e) => m + e.count, 0),
			0
		);
		const awaiting = (access?.capacity?.exceptions || []).filter((e) => e.key === 'awaitingReview');
		if (awaiting.length)
			actions.push({
				key: 'reviewInstallation',
				label: 'Review installation',
				owner: OWNER.PARTNER,
				deadline: null,
				count: awaiting.length
			});
		if (review > 0)
			actions.push({
				key: 'review',
				label: 'Review module assignments',
				owner: OWNER.PARTNER,
				deadline: null,
				count: review
			});
	}
	return actions;
};

// ── public entry ─────────────────────────────────────────────────────────────────────────────────

/**
 * @param {object}  args
 * @param {object|null} args.billing        selectBillingState (org-bound) — null when not loaded / other org
 * @param {'loading'|'loaded'|'error'|'refused'} args.status
 * @param {Object<string, boolean>} [args.moduleEnabled]  productKey → module flag on (configuration only)
 * @param {Object<string, string>}  [args.siteNames]      siteID → name
 * @param {{status: string, data: object|null}|null} [args.licensing]  selectCustomerLicensing (v1.15): used only when
 *   loaded and unredacted on a v1.14 org; 'hidden' (404), 'unavailable' and loading keep today's paths
 */
export const buildLicensingPresentation = ({
	billing,
	status,
	moduleEnabled = null,
	siteNames = {},
	licensing = null
}) => {
	const source = classifySource({ billing, status });
	const record = source === 'V114' ? licensingRecordOf(licensing) : null;
	const unknownReason = {
		UNAVAILABLE: 'Licensing information couldn’t be read.',
		REDACTED: 'Licence details are shown to Owners, Global Admins and Billing Admins.',
		NONE: 'No licensing record is available for this organisation.'
	}[source];

	let accessV114 = null;
	if (source === 'V114') accessV114 = accessFromAgreement(billing.agreement, siteNames, record);
	const moduleLines = source === 'V114' ? billing.moduleLines : null;

	const build = (entry, { isModule = false, accessDate = null } = {}) => {
		const base = {
			key: entry.key,
			productKey: entry.productKey,
			label: entry.label || null,
			codes: entry.codes,
			isModule,
			configuration: configurationOf(entry.productKey, moduleEnabled),
			edition: null,
			date: null,
			capacity: unknownCapacity(entry.unit),
			sites: [],
			assignments: []
		};
		let facts;
		if (entry.key === 'access' && !isModule && (source === 'NONE' || source === 'LEGACY_PLAN')) {
			// a legacy plan's own recorded trial is more specific than "Licensed": keep it, marked legacy
			const fromPlan = source === 'LEGACY_PLAN' ? legacyLicence(billing, entry.codes) : null;
			facts = {
				licence:
					fromPlan?.state === LICENCE_STATE.TRIAL
						? { ...fromPlan, legacy: true, label: `${fromPlan.label} · Legacy` }
						: legacyAccessLicence()
			};
			if (source === 'LEGACY_PLAN') facts.date = legacyDate(billing);
		} else if (source === 'V114') {
			if (entry.key === 'access') facts = accessV114;
			else if (!entry.inLicensingCatalogue && !isModule)
				facts = { licence: unknownLicence('Not in the licensing catalogue yet.') };
			else
				facts = perUnitFromLines(
					linesFor(moduleLines, entry.codes),
					entry.unit,
					accessDate,
					siteNames,
					record,
					entry.codes
				);
		} else if (source === 'LEGACY_PLAN' && entry.inLegacyCatalogue === false) {
			facts = { licence: unknownLicence('Not part of the Cloud plan catalogue.') };
		} else if (source === 'LEGACY_PLAN') {
			facts = { licence: legacyLicence(billing, entry.codes) };
			if (facts.licence.state !== LICENCE_STATE.NONE) facts.date = legacyDate(billing);
		} else facts = { licence: unknownLicence(unknownReason) };

		const item = { ...base, ...facts };
		item.trial = trialOf(entry.codes, item.licence.state, source === 'UNAVAILABLE' ? null : billing);
		return item;
	};

	const products = LICENSING_CATALOGUE.map((entry) => {
		const product = build({ ...entry, inLicensingCatalogue: entry.inLicensingCatalogue });
		product.modules = (entry.modules || []).map((m) =>
			build({ ...m, inLicensingCatalogue: true }, { isModule: true, accessDate: product.date })
		);
		return product;
	});

	const all = products.flatMap((p) => [p, ...p.modules]);
	const access = products.find((p) => p.key === 'access');

	return {
		source,
		// Phase from the v1.15 commitment state (attach / processing); UNKNOWN without a loaded record.
		phase: phaseOf(record),
		commitmentState: record ? record.state : null,
		payer: payerOf(source, billing),
		products,
		actions: actionsOf(source, billing, access, all),
		partnerManaged: source === 'V114' || source === 'REDACTED'
	};
};

/** Short tile/status text: licence label plus configuration (separate facts). */
export const statusLine = (item) => {
	const parts = [item.licence.label];
	if (item.configuration?.on === true) parts.push('On');
	else if (item.configuration?.on === false) parts.push('Off');
	return parts.join(' · ');
};

/**
 * True when a product gets a detail row: only when it is agreed, licensed, in a trial or in grace (GB 2026-11-03: the
 * product range is shown once, in the strip; a Not confirmed or Not licensed product has no row, and its On/Off stays
 * on its strip tile).
 */
export const HELD_STATES = Object.freeze([
	LICENCE_STATE.ONGOING,
	LICENCE_STATE.AGREED,
	LICENCE_STATE.TRIAL,
	LICENCE_STATE.GRACE
]);
export const showsCoverageRow = (product) => HELD_STATES.includes(product.licence.state);
